import * as XLSX from 'xlsx';
import { useCallback } from 'react';
import type { WorkSessionDetailed } from '@/lib/collections/datapicker/reports-assitance/fetchReport';

export function useMonthlyReportExcel(
  reportData: WorkSessionDetailed[],
  totalHours: number,
  year: number,
  month: number,
  userName: string
): () => void {
  return useCallback(() => {
    const timeZone = 'America/Guayaquil';

    // calculamos cuantos breaks hay como máximo
    const maxBreaks = reportData.reduce((max, s) => Math.max(max, s.breaks.length), 0);

    // creamos el header de la hoja de Sesiones
    const header: string[] = [
      '#',
      'Fecha',
      'Inicio',
      'Fin',

      // 'Duración (Horas)',
      'Duración (hh:mm:ss)',
      '',
      ...Array.from({ length: maxBreaks }).flatMap((_, i) => [
        `Break ${i + 1} Inicio`,
        `Break ${i + 1} Fin`,
        `Break ${i + 1} Duración (hh:mm)`,
      ]),
    ];

    // Construimos las filas de Sesiones + Breaks
    const wsSessions: any[][] = [header];
    let totalMinutes = 0;

    reportData.forEach((s, idx) => {
      const start = new Date(s.clockIn);
      const end = s.clockOut ? new Date(s.clockOut) : start;
      const diffMs = end.getTime() - start.getTime();
      // const diffH = diffMs / (1000 * 60 * 60);
      const diffTotalSec = Math.floor(diffMs / 1000);
      const hours = Math.floor(diffTotalSec / 3600);
      const minutes = Math.floor((diffTotalSec % 3600) / 60);
      const seconds = diffTotalSec % 60;
      const durationHHMMSS = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      const diffM = Math.round(diffMs / (1000 * 60));
      totalMinutes += diffM;

      // Fila base de la sesión
      const baseRow: any[] = [
        idx + 1,
        start.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone }),
        start.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone }),
        s.clockOut
          ? end.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone })
          : 'S/H',
        // diffH.toFixed(2),
        durationHHMMSS,
        '',
      ];

      // añadimos celdas de cada break
      const breakCells = Array.from({ length: maxBreaks }).flatMap((_, bi) => {
        const b = s.breaks[bi];
        if (!b) return ['', '', ''];

        const bStart = new Date(b.startTime);
        const bEnd = b.endTime ? new Date(b.endTime) : bStart;
        const sec = Math.round((bEnd.getTime() - bStart.getTime()) / 1000);
        const hh = Math.floor(sec / 3600);
        const mm = Math.floor((sec % 3600) / 60);
        // const ss = sec % 60;
        totalMinutes += Math.floor(sec / 60);

        const hhStr = String(hh).padStart(2, '0');
        const mmStr = String(mm).padStart(2, '0');
        // const ssStr = String(ss).padStart(2, '0');

        return [
          bStart.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone }),
          b.endTime
            ? bEnd.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone })
            : 'Activo',
          // `${hhStr}:${mmStr}:${ssStr}`,
          `${hhStr}:${mmStr}`,
        ];
      });

      wsSessions.push([...baseRow, ...breakCells]);
    });

    // Añadimos filas de totales
    const totalSeconds = Math.round(totalHours * 3600);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const totalHHMMSS = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    wsSessions.push([]);
    wsSessions.push([]);
    // wsSessions.push([
    //   'Total horas',
    //   '', '', '', totalHours.toFixed(2),
    //   ...Array(maxBreaks * 3).fill(''),
    // ]);

    wsSessions.push([
      'Total(hh:mm:ss)',
      '', '', '', totalHHMMSS,
      ...Array(maxBreaks * 3).fill(''),
    ]);



    // construimos la hoja de Notas 
    const wsNotes: any[][] = [
      ['Sesión #', 'Fecha', 'Hora', 'Nota'],
      ...reportData.flatMap((s, i) =>
        s.notes.map(n => {
          const dt = new Date(n.createdAt);
          return [
            i + 1,
            dt.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone }),
            dt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone }),
            n.noteContent,
          ];
        })
      ),
    ];

    //generamos el workbook y añadimos ambas hojas
    const wb = XLSX.utils.book_new();

    // Ajustar automáticamente el ancho de las columnas según el contenido
    const wsSheet = XLSX.utils.aoa_to_sheet(wsSessions);
    const colWidths = wsSessions[0].map((_, colIndex) => {
      const maxLength = wsSessions.reduce((max, row) => {
        const cell = row[colIndex];
        const len = cell ? cell.toString().length : 0;
        return Math.max(max, len);
      }, 10); // minimo de ancho de columna
      return { wch: maxLength + 2 }; // +2 para margen
    });

    wsSheet['!cols'] = colWidths;
    XLSX.utils.book_append_sheet(wb, wsSheet, 'Sesiones');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wsNotes), 'Notas');

    // fire la descarga
    XLSX.writeFileXLSX(wb, `reporte_${userName}_${String(month).padStart(2, '0')}_${year}.xlsx`);
  }, [reportData, totalHours, year, month, userName]);
}
